import React, { useCallback, useEffect, useState } from 'react';
import './Styles.css';
import { ServiceClient } from '../../../../../Services/ServiceClient';
import Modal from '../../../../../Elements/ids/Modal';
import LoaderComponent from '../../../../../Elements/ids/Loader';
import { formatMessage } from '../../../../../Localization/formatMessage';
import Messages from '../../../../../Localization/Messages';
import { PresentationMode } from '../../../../../Enums/PresentationMode';
import EditAgentBankAccountButtons from './Buttons/';
import BankAccountTypeSelector from '../../../../../Elements/BankAccountTypeSelector';
import TextInput from '../../../../../Elements/ids/InputElements/TextInput';
import Form from '../../../../../Elements/Form';
import {
  createServerErrorMessage,
  getCommonInputFields,
  getErrorMessage,
} from '../../../../../Helpers/FormInputHelper';
import ErrorMessage from '../../../../../Elements/ErrorMessage';
import { haveFieldsChanged, validateBankAccount } from './Functions';
import { useDebouncedEffect } from '../../../../../Helpers/CustomHooks';

export const editAgentBankAccountFormId = 'editAgentBankAccountForm';

const EditAgentBankAccount = ({ closeView, agentCode, teamBankAccount }) => {
  const [modalMode, setModalMode] = useState(PresentationMode.Loading);
  const [bankDetails, setBankDetails] = useState({});
  const [editedBankDetails, setEditedBankDetails] = useState({});
  const [canSubmit, setCanSubmit] = useState(false);
  const [errors, setErrors] = useState([]);

  const getCommonFields = (propertyName, isMandatory) => {
    return getCommonInputFields(editedBankDetails, setEditedBankDetails, propertyName, errors, isMandatory);
  };

  useEffect(() => {
    ServiceClient.getBankAccountForAgent(agentCode, data => {
      if (data.bankAccount) {
        setBankDetails(data.bankAccount);
        setEditedBankDetails(data.bankAccount);
        setModalMode(PresentationMode.Edit);
      } else if (teamBankAccount) {
        setBankDetails(teamBankAccount);
        setEditedBankDetails(teamBankAccount);
        setModalMode(PresentationMode.Edit);
      } else {
        setModalMode(PresentationMode.Warning);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (event, bankDetails) => {
    event.preventDefault();

    ServiceClient.setBankAccountForAgent(
      agentCode,
      bankDetails.number,
      bankDetails.type,
      () => {
        closeView();
      },
      error => {
        setModalMode(PresentationMode.Error);
        setErrors([createServerErrorMessage(error, editAgentBankAccountFormId)]);
        setCanSubmit(false);
      }
    );
  };

  const validateEditAgentBankAccount = useCallback(() => {
    const errors = validateBankAccount(editedBankDetails);
    setErrors(errors);
    setCanSubmit(errors.length === 0 && haveFieldsChanged(bankDetails, editedBankDetails));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedBankDetails]);

  useDebouncedEffect(validateEditAgentBankAccount, 500);

  return (
    <Modal
      closeView={closeView}
      titleText={formatMessage(Messages.agentBankAccount_title, {
        agentCode,
      })}
      buttons={
        <EditAgentBankAccountButtons
          closeView={closeView}
          canSubmit={canSubmit}
          modalMode={modalMode}
          closeError={() => {
            setModalMode(PresentationMode.Edit);
            setErrors([]);
          }}
        />
      }>
      <div className="team-page_agent-bank_container">
        {modalMode === PresentationMode.Loading ? (
          <LoaderComponent />
        ) : modalMode === PresentationMode.Warning ? (
          <ErrorMessage message={formatMessage(Messages.agentBankAccount_teamBankAccountNotSet)} />
        ) : modalMode === PresentationMode.Error ? (
          <ErrorMessage message={getErrorMessage(editAgentBankAccountFormId, errors)} />
        ) : (
          <Form
            formId={editAgentBankAccountFormId}
            onSubmit={e => {
              onSubmit(e, editedBankDetails);
            }}
            disabled={modalMode === PresentationMode.Error}>
            <BankAccountTypeSelector
              bankAccountType={editedBankDetails?.type}
              onChange={type => {
                setEditedBankDetails({
                  ...editedBankDetails,
                  type: type,
                });
              }}
              errorMessage={getErrorMessage('type', errors)}
            />
            <TextInput {...getCommonFields('number', true)} label={formatMessage(Messages.common_bankAccount)} />
          </Form>
        )}
      </div>
    </Modal>
  );
};

export default EditAgentBankAccount;
